// @ts-nocheck - Temporary fix for backend integration types
import { authHttpClient, publicHttpClient } from '@/lib/httpClient';
import { API_CONFIG, buildAuthApiUrl } from '@/config/api';
import { CategoryCacheService } from './categoryCache';
import type { Job, JobCategory } from '@/types';

export class BackendJobService {
  private getAuthToken(): string | undefined {
    return localStorage.getItem('auth_token') || undefined;
  }

  private getUserId(): string | undefined {
    return localStorage.getItem('user_id') || undefined;
  }

  // Job Management
  async createJob(jobData: {
    title: string;
    description: string;
    category_id: string;
    subcategory?: string;
    location: string;
    priority: string;
    budget_min?: number;
    budget_max?: number;
    scheduled_date?: string;
    property_type?: string;
    access_instructions?: string;
    parking_available?: boolean;
    preferred_time?: string;
    flexible?: boolean;
    requirements?: string[];
    additional_details?: string;
    contact_method?: string;
    provider_count?: number;
    preferred_provider?: string;
    house_number?: string;
    street_address?: string;
    town?: string;
    county?: string;
    eir_code?: string;
    images?: string[];
  }): Promise<Job> {
    console.log('🔄 Creating job with data:', jobData);

    // 🚨 DEBUG: Log the exact URL being called
    const url = buildAuthApiUrl(API_CONFIG.ENDPOINTS.JOBS);
    const token = this.getAuthToken();
    console.log('🎯 DEBUG: Calling URL:', url);
    console.log('🎯 DEBUG: Token exists:', !!token);
    console.log('🎯 DEBUG: Token preview:', token?.substring(0, 20) + '...');

    const response = await authHttpClient.post(buildAuthApiUrl(API_CONFIG.ENDPOINTS.JOBS), jobData, {
      token: this.getAuthToken()
    });

    if (!response.success) {
      console.error('❌ Job creation failed:', response.error);
      throw new Error(response.error || 'Failed to create job');
    }

    console.log('✅ Job created successfully:', response.data);
    return this.mapJobFromBackend(response.data);
  }

  async getJobs(filters?: {
    status?: string;
    category?: string;
    location?: string;
    userId?: string;
    page?: number;
    limit?: number;
  }): Promise<{ jobs: Job[]; total: number; page: number; totalPages: number }> {
    const params: Record<string, string> = {};

    if (filters?.status) params.status = filters.status;
    if (filters?.category) params.category = filters.category;
    if (filters?.location) params.location = filters.location;
    if (filters?.userId) params.user_id = filters.userId;
    if (filters?.page) params.page = filters.page.toString();
    if (filters?.limit) params.limit = filters.limit.toString();

    const response = await authHttpClient.get(API_CONFIG.ENDPOINTS.JOBS, {
      params,
      token: this.getAuthToken()
    });

    if (!response.success) {
      throw new Error(response.error || 'Failed to fetch jobs');
    }

    return {
      jobs: (response.data.jobs || response.data || []).map((job: any) => this.mapJobFromBackend(job)),
      total: response.data.total || 0,
      page: response.data.page || 1,
      totalPages: response.data.totalPages || 1
    };
  }

  async getMyJobs(): Promise<{ jobs: Job[]; total: number; page: number; totalPages: number }> {
    const response = await authHttpClient.get(buildAuthApiUrl(API_CONFIG.ENDPOINTS.JOB_MY_JOBS), {
      token: this.getAuthToken()
    });

    if (!response.success) {
      throw new Error(response.error || 'Failed to fetch your jobs');
    }

    // Backend returns array directly for my-jobs endpoint
    const jobs = Array.isArray(response.data) ? response.data : [];

    return {
      jobs: jobs.map((job: any) => this.mapJobFromBackend(job)),
      total: jobs.length,
      page: 1,
      totalPages: 1
    };
  }

  async getJobById(jobId: string): Promise<Job | null> {
    const response = await authHttpClient.get(`${buildAuthApiUrl(API_CONFIG.ENDPOINTS.JOBS)}/${jobId}`, {
      token: this.getAuthToken()
    });

    if (!response.success) {
      if (response.error?.includes('not found')) return null;
      throw new Error(response.error || 'Failed to fetch job');
    }

    return this.mapJobFromBackend(response.data);
  }

  // Provider-specific method to get job details (customer details hidden until quote accepted)
  async getProviderJobById(jobId: string): Promise<Job | null> {
    console.log('🔍 getProviderJobById called for:', jobId);
    const url = buildAuthApiUrl(`/providers/jobs/${jobId}`);
    console.log('📡 Calling URL:', url);
    
    const response = await authHttpClient.get(
      url,
      { token: this.getAuthToken() }
    );

    console.log('📥 Response:', response);

    if (!response.success) {
      console.error('❌ API error:', response.error);
      if (response.error?.includes('not found')) return null;
      throw new Error(response.error || 'Failed to fetch job');
    }

    const mappedJob = this.mapJobFromBackend(response.data);
    console.log('✅ Mapped job:', mappedJob);
    return mappedJob;
  }

  async updateJob(jobId: string, updates: Partial<{
    title: string;
    description: string;
    category: string;
    location: string;
    urgency: string;
    budget: any;
    requirements: string[];
    preferredDate: string;
    status: string;
  }>): Promise<Job> {
    const response = await authHttpClient.put(`${buildAuthApiUrl(API_CONFIG.ENDPOINTS.JOBS)}/${jobId}`, updates, {
      token: this.getAuthToken()
    });

    if (!response.success) {
      throw new Error(response.error || 'Failed to update job');
    }

    return this.mapJobFromBackend(response.data);
  }

  async deleteJob(jobId: string): Promise<void> {
    const response = await authHttpClient.delete(`${buildAuthApiUrl(API_CONFIG.ENDPOINTS.JOBS)}/${jobId}`, {
      token: this.getAuthToken()
    });

    if (!response.success) {
      throw new Error(response.error || 'Failed to delete job');
    }
  }

  // Update job status (NEW - connects to PUT /api/v1/auth/jobs/{id}/status)
  async updateJobStatus(jobId: string, status: string): Promise<Job> {
    console.log('🔄 Updating job status:', { jobId, status });

    const response = await authHttpClient.put(
      `${buildAuthApiUrl(API_CONFIG.ENDPOINTS.JOBS)}/${jobId}/status`,
      { status },
      { token: this.getAuthToken() }
    );

    if (!response.success) {
      console.error('❌ Job status update failed:', response.error);
      throw new Error(response.error || 'Failed to update job status');
    }

    console.log('✅ Job status updated successfully:', response.data);
    return this.mapJobFromBackend(response.data);
  }

  // Assign job to provider (NEW - connects to POST /api/v1/auth/jobs/{id}/assign)
  async assignJob(jobId: string, providerId: string): Promise<Job> {
    console.log('🔄 Assigning job to provider:', { jobId, providerId });

    const response = await authHttpClient.post(
      `${buildAuthApiUrl(API_CONFIG.ENDPOINTS.JOBS)}/${jobId}/assign`,
      { provider_id: providerId },
      { token: this.getAuthToken() }
    );

    if (!response.success) {
      console.error('❌ Job assignment failed:', response.error);
      throw new Error(response.error || 'Failed to assign job');
    }

    console.log('✅ Job assigned successfully:', response.data);
    return this.mapJobFromBackend(response.data);
  }

  // Job Categories
  async getJobCategories(): Promise<JobCategory[]> {
    const response = await publicHttpClient.get(API_CONFIG.ENDPOINTS.JOB_CATEGORIES, {
      auth: false // 🎯 EXPLICIT: This is a public endpoint
      // Categories are public endpoints, no auth required
      // token: this.getAuthToken()
    });

    if (!response.success) {
      throw new Error(response.error || 'Failed to fetch job categories');
    }

    // Handle both data.data and direct data response formats
    const categories = response.data.data || response.data || [];

    console.log('✅ Categories loaded:', categories.length, 'categories');

    // Map backend category format to frontend format
    const mappedCategories = categories.map((cat: any) => ({
      id: cat.id,
      name: cat.name,
      slug: cat.slug,
      description: cat.description,
      icon: cat.icon,
      color: cat.color,
      displayOrder: cat.display_order
    }));

    console.log('📋 Mapped categories:', mappedCategories);
    return mappedCategories;
  }

  // New method: Get homepage categories with graceful loading (cache-first, background refresh)
  async getHomepageCategories(): Promise<any[]> {
    try {
      // ALWAYS try cache first for instant loading
      const cached = CategoryCacheService.getCachedCategories();
      if (cached && cached.length > 0) {
        console.log('⚡ Using cached homepage categories for instant loading:', cached.length);

        // Background refresh: silently try to update cache without blocking UI
        this.refreshHomepageCategoriesInBackground();

        return cached;
      }

      // If no cache, try expired cache for instant loading
      const expiredCache = CategoryCacheService.getCachedCategories(true);
      if (expiredCache && expiredCache.length > 0) {
        console.log('⚡ Using expired cache for instant loading:', expiredCache.length);

        // Background refresh: silently try to update cache
        this.refreshHomepageCategoriesInBackground();

        return expiredCache;
      }

      // No cache available, try API once (silently)
      console.log('🔄 No cache available, fetching from API...');
      return await this.fetchHomepageCategoriesFromAPI();

    } catch (error) {
      // Silent error handling - never show errors to user
      console.warn('⚠️ Silent error in getHomepageCategories:', error);

      // Return empty array gracefully
      return [];
    }
  }

  // Background refresh method (runs silently)
  private async refreshHomepageCategoriesInBackground(): Promise<void> {
    try {
      console.log('🔄 Background refresh: Updating categories cache...');
      const freshData = await this.fetchHomepageCategoriesFromAPI();

      if (freshData && freshData.length > 0) {
        console.log('✅ Background refresh: Cache updated with', freshData.length, 'categories');
        // Cache is automatically updated in fetchHomepageCategoriesFromAPI
      }
    } catch (error) {
      // Silent background error - don't affect user experience
      console.warn('⚠️ Background refresh failed (silent):', error);
    }
  }

  // Core API fetch method (used by both foreground and background)
  private async fetchHomepageCategoriesFromAPI(): Promise<any[]> {
    const response = await publicHttpClient.get(`${API_CONFIG.ENDPOINTS.JOB_CATEGORIES}/homepage`, {
      auth: false // 🎯 EXPLICIT: This is a public endpoint
    });

    if (!response.success) {
      // Silent failure - don't throw errors
      console.warn('⚠️ API call failed silently:', response.error);
      return [];
    }

    const categories = response.data.data || response.data || [];

    if (!Array.isArray(categories) || categories.length === 0) {
      console.warn('⚠️ API returned empty/invalid data');
      return [];
    }

    const mappedCategories = categories.map((cat: any) => ({
      id: cat.id,
      name: cat.name,
      slug: cat.slug,
      description: cat.description,
      icon_name: cat.icon_name,
      color_from: cat.color_from,
      color_to: cat.color_to,
      image_url: cat.image_url,
      homepage_ranking: cat.homepage_ranking,
      job_count: cat.job_count || 0
    }));

    // Cache the results
    CategoryCacheService.setCachedCategories(mappedCategories);

    console.log('✅ Fresh categories loaded and cached:', mappedCategories.length);
    return mappedCategories;
  }

  // New method: Get subcategories for a category with graceful loading
  async getCategorySubcategories(categoryId: string): Promise<any[]> {
    try {
      // ALWAYS try cache first for instant loading
      const cached = CategoryCacheService.getCachedSubcategories(categoryId);
      if (cached && cached.length > 0) {
        console.log('⚡ Using cached subcategories for instant loading, category', categoryId, ':', cached.length);

        // Background refresh: silently try to update cache
        this.refreshSubcategoriesInBackground(categoryId);

        return cached;
      }

      // If no cache, try expired cache for instant loading
      const expiredCache = CategoryCacheService.getCachedSubcategories(categoryId, true);
      if (expiredCache && expiredCache.length > 0) {
        console.log('⚡ Using expired subcategories cache for instant loading, category', categoryId, ':', expiredCache.length);

        // Background refresh: silently try to update cache
        this.refreshSubcategoriesInBackground(categoryId);

        return expiredCache;
      }

      // No cache available, try API once (silently)
      console.log('🔄 No subcategories cache available, fetching from API for category:', categoryId);
      return await this.fetchSubcategoriesFromAPI(categoryId);

    } catch (error) {
      // Silent error handling - never show errors to user
      console.warn('⚠️ Silent error in getCategorySubcategories:', error);

      // Return empty array gracefully
      return [];
    }
  }

  // Background refresh method for subcategories (runs silently)
  private async refreshSubcategoriesInBackground(categoryId: string): Promise<void> {
    try {
      console.log('🔄 Background refresh: Updating subcategories cache for category:', categoryId);
      const freshData = await this.fetchSubcategoriesFromAPI(categoryId);

      if (freshData && freshData.length > 0) {
        console.log('✅ Background refresh: Subcategories cache updated for category', categoryId, 'with', freshData.length, 'items');
      }
    } catch (error) {
      // Silent background error - don't affect user experience
      console.warn('⚠️ Background subcategories refresh failed (silent) for category', categoryId, ':', error);
    }
  }

  // Core API fetch method for subcategories
  private async fetchSubcategoriesFromAPI(categoryId: string): Promise<any[]> {
    console.log('🔍 DEBUG: fetchSubcategoriesFromAPI called for category:', categoryId);

    const response = await publicHttpClient.get(`${API_CONFIG.ENDPOINTS.JOB_CATEGORIES}/${categoryId}/subcategories`, {
      auth: false // 🎯 EXPLICIT: This is a public endpoint
    });

    if (!response.success) {
      // Silent failure - don't throw errors
      console.warn('⚠️ Subcategories API call failed silently for category', categoryId, ':', response.error);
      return [];
    }

    const subcategories = response.data.data || response.data || [];

    if (!Array.isArray(subcategories)) {
      console.warn('⚠️ Subcategories API returned invalid data for category', categoryId);
      return [];
    }

    const mappedSubcategories = subcategories.map((subcat: any) => ({
      id: subcat.id,
      category_id: subcat.category_id,
      name: subcat.name,
      slug: subcat.slug,
      description: subcat.description,
      job_count: subcat.job_count || 0
    }));

    // Cache the results
    CategoryCacheService.setCachedSubcategories(categoryId, mappedSubcategories);

    console.log('✅ Fresh subcategories loaded and cached for category', categoryId, ':', mappedSubcategories.length);
    return mappedSubcategories;
  }

  // Helper method to map backend job data to frontend Job type
  private mapJobFromBackend(data: any): Job {
    // Map backend priority to frontend urgency
    const mapPriorityToUrgency = (priority: string): string => {
      switch (priority?.toLowerCase()) {
        case 'low': return 'LOW';
        case 'medium': return 'MEDIUM';
        case 'high': return 'HIGH';
        default: return 'MEDIUM';
      }
    };

    // Map backend status to frontend status (uppercase)
    const mapStatus = (status: string): string => {
      if (!status) return 'OPEN';
      const normalized = status.toLowerCase().replace(/_/g, '-');
      switch (normalized) {
        case 'open': return 'OPEN';
        case 'quote-accepted': return 'QUOTE_ACCEPTED';
        case 'assigned': return 'IN_PROGRESS';
        case 'in-progress': return 'IN_PROGRESS'; // Handle hyphen format from backend
        case 'in_progress': return 'IN_PROGRESS'; // Handle underscore format
        case 'completed': return 'COMPLETED';
        case 'cancelled': return 'CANCELLED';
        default: return 'OPEN';
      }
    };

    return {
      id: data.id,
      title: data.title,
      description: data.description,
      category: data.category_name || data.category || 'General', // Map category_id to category name
      location: data.location,
      urgency: mapPriorityToUrgency(data.priority), // Map priority to urgency with proper case
      status: mapStatus(data.status), // Map status with proper case
      customerId: data.customer_id || data.customerId,
      customerName: data.customer_name || data.customerName || 'Customer',
      assignedProviderId: data.provider_id || data.providerId || data.assignedProviderId,
      providerId: data.provider_id || data.providerId, // Keep for backward compatibility
      providerName: data.provider_name || data.providerName,
      budgetRange: data.budget_min || data.budget_max ? {
        min: data.budget_min || 0,
        max: data.budget_max || 0
      } : undefined,
      budgetMin: data.budget_min,
      budgetMax: data.budget_max,
      requirements: data.requirements || [],
      images: data.images || [],
      preferredDate: data.scheduled_date ? new Date(data.scheduled_date) : undefined,
      scheduledDate: data.scheduled_date ? new Date(data.scheduled_date) : undefined,
      selectedDateSlot: data.selected_date_slot,
      contactPreference: data.contact_preference || data.contactPreference || 'app',
      bidCount: data.bid_count || data.bidCount || 0,
      quoteCount: data.quote_count || data.quoteCount || 0, // Add quoteCount mapping
      viewCount: data.view_count || data.viewCount || 0,
      isBookmarked: data.is_bookmarked || data.isBookmarked || false,
      createdAt: new Date(data.created_at),
      updatedAt: new Date(data.updated_at),
      completedAt: data.completed_at ? new Date(data.completed_at) : undefined
    };
  }
}

export const backendJobService = new BackendJobService();